// AISplitPane.jsx
import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import "./aISplitPane.scss";

import {
  SplitPaneControl,
  TabPaneControl,
  IconControl,
  ToastNotifications,
  FieldControl,
  ButtonControl,
  Loading,
  DialogControl,
  DragOverlay
} from "spark-core-dx/components";

import { useGenericManager } from "spark-core-dx/hooks";
import AIChatControl from "./AIChatControlV1";
import { ItemServiceFactory } from "../../services/ItemServiceFactory";
import { SystemTypes } from "spark-core-dx/services";
import AIChatDetailsControl from "./AIChatDetailsControl";

/* -------------------- Rotating technical “thinking” messages -------------------- */
const DEFAULT_INIT_MESSAGES = [
  "Loading dataset slices relevant to your question…",
  "Verifying schema and field roles for analysis…",
  "Detecting date ranges and active time windows…",
  "Normalizing units, currencies, and locales…",
  "Scanning for missing values and safe defaults…",
  "De-duplicating records and resolving keys…",
  "Validating joins and relationship paths…",
  "Indexing columns used for filters and grouping…",
  "Sampling rows to estimate result size…",
  "Precomputing candidate aggregates (sum/avg/min/max)…",
  "Identifying potential outlier metrics and thresholds…",
  "Applying small-result policies to cap row output…",
  "Assembling grouping fields to match the ask…",
  "Synthesizing filters from the question intent…",
  "Selecting measures most aligned with the prompt…",
  "Projecting expected answer shape (single value or short list)…",
  "Priming cache for repeated aggregations…",
  "Final consistency check on query constraints…",
  "Preparing compact result payload for rendering…",
  "Handover complete — executing analysis…"
];

function mulberry32(seed) {
  let t = seed >>> 0;
  return function () {
    t += 0x6D2B79F5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffle(arr, seed) {
  const a = arr.slice();
  const rand = typeof seed === "number" ? mulberry32(seed) : Math.random;
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor((typeof rand === "function" ? rand() : Math.random) * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function createMessageRotator({ messages = DEFAULT_INIT_MESSAGES, seed } = {}) {
  let pool = shuffle(messages, seed);
  let idx = 0;
  return {
    next() {
      if (idx >= pool.length) {
        pool = shuffle(messages, (seed ?? 0) + 1);
        idx = 0;
      }
      return pool[idx++];
    },
    reset(newSeed = seed) {
      pool = shuffle(messages, newSeed);
      idx = 0;
    }
  };
}
/* ------------------------------------------------------------------------------ */

export const AISplitPane = ({
  className,
  children,
  onUpdatePanel,
  session,                    // if provided and has _id, we will load & start this session (no autostart)
  referenceId,
  referenceName,
  params,
  operationName,
  enableContentDragHiding = true,
  startChat = null,           // if string (non-empty): auto-start chat and send this as the first message (only when no session id)
  chatPane = "right",         // 'right' | 'middle' | 'left'
  payload = null,
  // pane inputs can be React nodes or tabs [{ key, title|label, content }]
  left: leftInput,
  middle: middleInput,
  right: rightInput,

  // SplitPane sizing
  storageKey = "ai-panel-left-width",
  initialRightPct = 0.33,
  initialLeftPct = 0.67,
  minLeft = 360,
  minMiddle = 360,
  minRight = 360,
  splashMessage = null,
  introMessage = null
}) => {
  // ---- UI State ----
  const [uiMode, setUiMode] = useState("startup"); // 'startup' | 'chat'
  const [isLoadingHistory, setIsLoadingHistory] = useState(false); // gate visual switch until history finishes
  const [toasterMessage, setToasterMessage] = useState(null);
  const [sessionChatMessages, setSessionChatMessages] = useState(null); // null = empty, undefined = loading, array = loaded
  const [chatSession, setChatSession] = useState(null);
  const [thinking, setThinking] = useState("");
  const [chatApi, setChatApi] = useState();
  const [jsonData, setJsonData] = useState([]);
  const [allChats, setAllChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [showChatDialog, setShowChatDialog] = useState(false);
  const [dragging, setDraging] = useState(false);


  // Controlled dropdown selection (do NOT derive from chatSession to avoid flicker)
  const [selectedSessionId, setSelectedSessionId] = useState(null);

  // Auto-start lifecycle
  const [pendingAutoPrompt, setPendingAutoPrompt] = useState(null);
  const [didAutoStart, setDidAutoStart] = useState(false);
  const autoStartInProgressRef = useRef(false);
  const initSeededRef = useRef(false);
  const [chatUiReady, setChatUiReady] = useState(false);

  // Guards
  const inFlightSendRef = useRef(false);
  const hasAutoStartedRef = useRef(null);

  // Load dedupe & state
  const loadingSessionsRef = useRef(new Set()); // ids currently loading
  const lastLoadedIdRef = useRef(null);

  // Sticky “user chose a session here” flag — prevents parent !session from resetting UI
  const localPinnedRef = useRef(false);

  const chatRef = useRef(null);

  // Rotating thinking/status messages
  const thinkingRotatorRef = useRef(createMessageRotator({ seed: 17 }));
  const nextThinking = useCallback(() => thinkingRotatorRef.current.next(), []);

  const itemServiceFactory = useMemo(() => new ItemServiceFactory(), []);
  const aiSessionMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.AIChatSession);
  const aiChatMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.AIChat);

  // ---- helpers (per-session localStorage, not backward compatible) ----
  const LS_PREFIX = "ai:msgs:";
  const getMsgsKey = (id) => (id ? `${LS_PREFIX}${id}` : null);
  const getStoredMessages = (id) => {
    if (!id) return null;
    try {
      const raw = localStorage.getItem(getMsgsKey(id));
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  };
  const setStoredMessages = (id, messages) => {
    if (!id) return;
    try {
      localStorage.setItem(getMsgsKey(id), JSON.stringify(messages ?? []));
    } catch {}
  };

  const hasNonSystemMessages = (msgs) =>
    Array.isArray(msgs) &&
    msgs.some(
      (m) =>
        (m?.role || "").toLowerCase() !== "system" &&
        (m?.content || "").trim() !== ""
    );

  const isTabsArray = (val) =>
    Array.isArray(val) &&
    val.length > 0 &&
    val.every(
      (t) =>
        t &&
        typeof t === "object" &&
        typeof t.key === "string" &&
        (typeof t.title === "string" || typeof t.label === "string") &&
        "content" in t
    );

  const upsertById = (list, item) => {
    const arr = Array.isArray(list) ? list : [];
    const idx = arr.findIndex((x) => x && x._id === item._id);
    if (idx === -1) return [...arr, item];
    const clone = arr.slice();
    clone[idx] = { ...clone[idx], ...item };
    return clone;
  };

  const first = (x) =>
    typeof x?.first === "function" ? x.first() : Array.isArray(x) ? x[0] : undefined;

  const formatError = (r) => {
    const getPropCI = (obj, ...names) => {
      if (!obj || typeof obj !== "object") return undefined;
      const keyMap = new Map(Object.keys(obj).map((k) => [k.toLowerCase(), k]));
      for (const n of names) {
        const k = keyMap.get(String(n).toLowerCase());
        if (k !== undefined) return obj[k];
      }
      return undefined;
    };
    const asCsv = (arr) => {
      if (!Array.isArray(arr)) return undefined;
      return arr
        .map((x) => {
          if (x == null) return "";
          if (typeof x === "string") return x;
          if (typeof x === "object") {
            const inner = extractDetail(x);
            return inner != null && inner !== ""
              ? String(inner)
              : JSON.stringify(inner);
          }
          return String(x);
        })
        .filter(Boolean)
        .join(", ");
    };
    const extractDetail = (obj) => {
      if (obj == null) return undefined;
      if (typeof obj !== "object") return String(obj);
      const vDisplay = getPropCI(obj, "displaymessage");
      if (vDisplay != null)
        return typeof vDisplay === "object"
          ? extractDetail(vDisplay)
          : String(vDisplay);
      const vMessage = getPropCI(obj, "message");
      if (vMessage != null)
        return Array.isArray(vMessage)
          ? asCsv(vMessage) ?? String(vMessage)
          : typeof vMessage === "object"
          ? extractDetail(vMessage)
          : String(vMessage);
      const vError = getPropCI(obj, "error");
      if (vError != null)
        return typeof vError === "object"
          ? extractDetail(vError)
          : String(vError);
      const vErrMsg = getPropCI(obj, "errormessage");
      if (vErrMsg != null)
        return typeof vErrMsg === "object"
          ? extractDetail(vErrMsg)
          : String(vErrMsg);
      const vMsgDetails = getPropCI(obj, "messagedetails");
      if (vMsgDetails != null)
        return Array.isArray(vMsgDetails)
          ? asCsv(vMsgDetails)
          : typeof vMsgDetails === "object"
          ? extractDetail(vMsgDetails)
          : String(vMsgDetails);
      const vInfo = getPropCI(obj, "info");
      if (vInfo != null)
        return typeof vInfo === "object"
          ? extractDetail(vInfo)
          : String(vInfo);
      const vDetails = getPropCI(obj, "details");
      if (vDetails != null)
        return Array.isArray(vDetails)
          ? asCsv(vDetails)
          : typeof vDetails === "object"
          ? extractDetail(vDetails)
          : String(vDetails);
      return undefined;
    };
    let label =
      getPropCI(r, "DisplayMessage", "AIOutcomeStatus", "Status") ?? "Error";
    if (typeof label === "object")
      label = extractDetail(label) ?? "Error";
    label = String(label);
    let detail = extractDetail(r);
    if (detail == null || detail === "") {
      const md = getPropCI(r, "MessageDetails");
      if (Array.isArray(md)) detail = asCsv(md);
    }
    if (detail == null) detail = getPropCI(r, "DisplayErrorMessage");
    if (detail == null) detail = "";

    return [label, String(detail)].filter(Boolean).join(": ");
  };

  // ---- Splash text helpers ----
  const getSplashText = () => {
    if (typeof splashMessage === "string" && splashMessage.trim().length > 0) {
      return splashMessage.trim();
    }
    if (referenceName) {
      return `Ask me a question about your ${referenceName}.`;
    }
    return "Ask me a question about your data.";
  };

  const makeSystemMessage = () => ({
    role: "system",
    content: getSplashText()
  });

  // ---------- Load an existing session by id & hydrate (dedup + lock) ----------
  const loadSessionById = useCallback(
    async (id, sessionObj = null) => {
      if (!id) return;

      // Pin locally; do not allow parent !session to reset UI while we're in control
      localPinnedRef.current = true;
      setUiMode("chat");

      // If this id already loaded successfully, bail.
      if (lastLoadedIdRef.current === id) {
        setSelectedSessionId(id);
        if (!chatSession)
          setChatSession({
            _id: id,
            ReferenceId: referenceId,
            ReferenceItemName: referenceName
          });
        return;
      }

      // In-flight protection
      if (loadingSessionsRef.current.has(id)) return;
      loadingSessionsRef.current.add(id);

      try {
        // reflect selection & create placeholder session (prevents snapping)
        setSelectedSessionId(id);

        if (!chatSession)
          setChatSession({
            _id: id,
            ReferenceId: referenceId,
            ReferenceItemName: referenceName
          });

        // per-session storage now; nothing to clear globally
        setSessionChatMessages(undefined); // loading
        setIsLoadingHistory(true); // gate UI switch

        // fetch chats (single call guarded by lock)
        const r = await aiChatMgr.Query({
          model:
            `x => x.IsActive == true && x.IsDeleted == false && x.ChatSessionId == sessionId`,
          params: { sessionId: id }
        });

        if (r?.Success) {
          const chats = (r?.Items ?? [])
            .slice()
            .sort((a, b) => (a?.Sequence ?? 0) - (b?.Sequence ?? 0))
            .flatMap((x) => [
              { role: "user", content: x?.Input ?? "", _id: x?._id },
              {
                role:
                  (x?.Role === "user" ? "assistant" : x?.Role) ?? "assistant",
                content: x?.ModelResponse ?? "",
                val: x,
                _id: x?._id
              }
            ])
            .filter((m) => (m?.content ?? "").trim() !== "");

          // hydrate session object
          if (sessionObj) {
            setChatSession(sessionObj);
          } else {
            const resp = await aiSessionMgr.getItem(id);
            if (resp?.Success) {
              setChatSession(
                resp.Items.first?.() ??
                  first(resp.Items) ??
                  resp.Items ?? {
                    _id: id,
                    ReferenceId: referenceId,
                    ReferenceItemName: referenceName
                  }
              );
            } else {
              setChatSession((prev) =>
                prev ?? {
                  _id: id,
                  ReferenceId: referenceId,
                  ReferenceItemName: referenceName
                }
              );
            }
          }

          setSessionChatMessages(chats);
          lastLoadedIdRef.current = id; // mark success
        } else {
          setSessionChatMessages([]); // keep chat mode
          setChatSession((prev) =>
            prev ?? { _id: id, ReferenceId: referenceId, ReferenceItemName: referenceName }
          );
        }
      } catch {
        setSessionChatMessages([]); // keep chat mode
        setChatSession((prev) =>
          prev ?? { _id: id, ReferenceId: referenceId, ReferenceItemName: referenceName }
        );
      } finally {
        setIsLoadingHistory(false);
        loadingSessionsRef.current.delete(id); // release lock
      }
    },
    [aiChatMgr, aiSessionMgr, chatSession, referenceId, referenceName]
  );

  // When the `session` prop changes:
  useEffect(() => {
    if (session && session._id) {
      setUiMode("chat");
      setChatSession(session);
      setSelectedSessionId(session._id);
      localPinnedRef.current = true; // respect parent-provided session as "pinned"
      if (lastLoadedIdRef.current !== session._id) {
        loadSessionById(session._id, session);
      }
      return;
    }

    if (session && !session._id) {
      setUiMode("chat");
      setChatSession(session);
      localPinnedRef.current = true;
      return;
    }

    // session is falsy
    if (localPinnedRef.current) {
      // Ignore transient parent clears while we control the selection
      return;
    }

    // Explicit clear from parent while not locally pinned → reset to startup
    setSessionChatMessages(null);
    setChatSession(null);
    setSelectedSessionId(null);
    lastLoadedIdRef.current = null;
    loadingSessionsRef.current.clear();
    setUiMode("startup");
  }, [session, loadSessionById]);

  // ---------- AI Chat plumbing ----------
  const handleInit = (api) => {
    if (!chatApi) setChatApi(api);

    if (initSeededRef.current) {
      setChatUiReady(true);
      return;
    }

    try {
      const knownId = session?._id || selectedSessionId;
      const saved = getStoredMessages(knownId);
      const hasSaved = Array.isArray(saved) && saved.length > 0;
      const hasSessionMsgs =
        Array.isArray(sessionChatMessages) && sessionChatMessages.length > 0;
      const hasStart =
        typeof startChat === "string" && startChat.trim().length > 0;

      if (hasSaved) {
        api.setMessages(saved);
      } else if (hasSessionMsgs) {
        api.setMessages(sessionChatMessages);
      } else if (!hasStart) {
        // seed with system-only splash, which will now match visible splash text
        api.setMessages([makeSystemMessage()]);
      }
    } catch {}

    initSeededRef.current = true;
    setChatUiReady(true);
  };

  // Keep UI in sync when history arrives after init
  useEffect(() => {
    if (!chatUiReady) return;
    if (!chatApi) return;
    if (Array.isArray(sessionChatMessages)) {
      const msgs =
        sessionChatMessages.length > 0
          ? sessionChatMessages
          : [makeSystemMessage()];
      chatApi.setMessages(msgs);
      const id = session?._id || selectedSessionId;
      setStoredMessages(id, msgs);
    }
  }, [chatUiReady, chatApi, sessionChatMessages]);

  const handleSave = (messages) => {
    const id = chatSession?._id || selectedSessionId;
    setStoredMessages(id, messages);
  };

  const handleSend = async (userText) => {
    if (inFlightSendRef.current)
      return { role: "assistant", content: "" };
    inFlightSendRef.current = true;
    try {
      let currentSession = chatSession;
      setThinking(nextThinking());

      if (currentSession?._id === undefined) {
        setThinking("Creating a new session...");
        const sessionResult = await aiSessionMgr.saveItem({
          ...currentSession,
          Name: "New Session"
        });
        currentSession =
          sessionResult?.Items?.first?.() ??
          first(sessionResult?.Items) ??
          sessionResult?.Items ??
          null;
        setChatSession(currentSession);
        setSelectedSessionId(currentSession?._id ?? null);
        localPinnedRef.current = true;
        setUiMode("chat");
      }

      const hubController = await aiSessionMgr.GetStream({
        operationName,
        id: currentSession?._id,
        type: "sse"
      });
      if (hubController?.Success && typeof hubController.onUI === "function") {
        hubController.onUI("thinking", (msg) => {
          const incoming = (msg?.Message ?? "").trim();
          if (!incoming || /^thinking\.{0,3}$/i.test(incoming)) {
            setThinking(nextThinking());
          } else {
            setThinking(incoming);
          }
        });
      } else {
        setThinking(nextThinking());
      }

      const chatResult = await aiSessionMgr.RunOperation({
        operationName,
        item: {
          sessionid: currentSession?._id,
          referenceid: referenceId,
          message: userText,
          ...(params ?? {}),
          payload
        },
        isItem: false
      });

      if (chatResult?.Success === false) {
        return { role: "error", content: formatError(chatResult) };
      }

      // UICommands (operation pathway)
      if (chatResult?.UICommands && chatResult.UICommands["AIAction"]) {
        try {
          if (onUpdatePanel)
            onUpdatePanel(
              chatResult.UICommands["AIAction"],
              chatResult?.Items?.length > 0
                ? chatResult?.Items.first()
                : null,
              chatResult?.Value
            );
        } catch {}
        setThinking(nextThinking());
        const item0 = first(chatResult?.Items) ?? {};
        const content =
          item0?.ResponseMessage ??
          (chatResult?.Value ? chatResult?.Value["comment"] : null) ??
          chatResult?.Value ??
          "Updating...";
        return { content, _id: item0?._id, role: item0?.Role || "assistant" };
      }

      // Normal assistant pathway
      setThinking(nextThinking());
      const item0 = first(chatResult?.Items) ?? {};
      const content =
        item0?.ModelResponse ??
        "Model did not respond with a valid value. Please contact support if this issue continues.";
      const role =
        item0?.DisplayErrorMessage != null
          ? "error"
          : item0?.Role &&
            String(item0.Role).toLowerCase() !== "user"
          ? item0.Role
          : "assistant";

      return { content, _id: item0?._id, role };
    } finally {
      inFlightSendRef.current = false;
    }
  };

  const handleOpenDetails = async (aiChatId) => {
    if (!aiChatId) return;
    setShowChatDialog(true);

    const cached = (allChats || []).find((c) => c && c._id === aiChatId);
    if (cached) {
      setSelectedChat(cached);
      setShowChatDialog(true);
      return;
    }

    const resp = await aiChatMgr.getItem(aiChatId);
    if (!resp?.Success || !resp.Items) return;

    const items = Array.isArray(resp.Items) ? resp.Items : [resp.Items];
    const item = items[0];
    if (!item?._id) return;

    setAllChats((prev) => upsertById(prev, item));
    setSelectedChat(item);
  };

  const handleStartChat = () => {
    if (!chatSession) {
      // per-session storage now; nothing to clear globally
      const seed = {
        ReferenceId: referenceId,
        ReferenceItemName: referenceName
      };
      setChatSession(seed);
      setSelectedSessionId(null);
      localPinnedRef.current = true;
      setUiMode("chat");
    }
  };

  // ---- Auto-start: ONLY when there's no provided session id, and not while history is loading
  useEffect(() => {
    if (!(typeof startChat === "string" && startChat.trim().length > 0)) return;
    if (session?._id) return;
    if (sessionChatMessages === undefined) return;

    const key = `${referenceId ?? "none"}|${operationName ?? "op"}|${startChat.trim()}`;
    if (hasAutoStartedRef.current === key) return;
    if (didAutoStart) return;

    const savedMsgs = getStoredMessages(session?._id || selectedSessionId);
    const anyExisting =
      hasNonSystemMessages(savedMsgs) ||
      (Array.isArray(sessionChatMessages) &&
        sessionChatMessages.length > 0);

    if (anyExisting) return;

    if (!chatSession) {
      setChatSession({
        ReferenceId: referenceId,
        ReferenceItemName: referenceName
      });
      localPinnedRef.current = true;
      setUiMode("chat");
    }

    setPendingAutoPrompt(startChat.trim());
    hasAutoStartedRef.current = key;
  }, [
    startChat,
    chatSession,
    didAutoStart,
    sessionChatMessages,
    referenceName,
    referenceId,
    operationName,
    session,
    selectedSessionId
  ]);

  // Once chat UI is ready, send the pending prompt WITHOUT seeding [system,user] beforehand
  useEffect(() => {
    const run = async () => {
      if (!pendingAutoPrompt) return;
      if (!chatSession) return;
      if (!chatUiReady) return;
      if (autoStartInProgressRef.current) return;
      if (didAutoStart) return;

      const savedMsgs = getStoredMessages(session?._id || selectedSessionId);
      const anyExisting =
        hasNonSystemMessages(savedMsgs) ||
        (Array.isArray(sessionChatMessages) &&
          sessionChatMessages.length > 0);
      if (anyExisting) {
        setDidAutoStart(true);
        setPendingAutoPrompt(null);
        return;
      }

      autoStartInProgressRef.current = true;
      setDidAutoStart(true);

      const userText = pendingAutoPrompt;

      try {
        const systemMsg = makeSystemMessage();
        const assistantMsg = await handleSend(userText);
        const assistant = Array.isArray(assistantMsg)
          ? assistantMsg[0]
          : assistantMsg;

        const msgs = [
          systemMsg,
          { role: "user", content: userText },
          assistant ?? { role: "assistant", content: "" }
        ];
        chatApi?.setMessages?.(msgs);
        const id = chatSession?._id || selectedSessionId;
        setStoredMessages(id, msgs);
        setPendingAutoPrompt(null);
        localPinnedRef.current = true;
        setUiMode("chat");
      } catch {
        setPendingAutoPrompt(null);
      }
    };
    run();
  }, [
    pendingAutoPrompt,
    chatSession,
    chatUiReady,
    didAutoStart,
    sessionChatMessages,
    session,
    selectedSessionId
  ]);

  // Populate session selector (only once per reference)
  useEffect(() => {
    if (jsonData.length === 0 && referenceId) {
      aiSessionMgr
        .GetSelectData({
          DataOperationItem: "AIChatSession",
          DataOperationApp: "Convoke",
          DisplayName: "Chat Session",
          FieldName: "_id"
        })
        .then((r) => {
          setJsonData((r?.jsonData || []).filter((x) => x.FilterValue === referenceId));
        });
    }
  }, [referenceId]);

  // --- Robust id coercion for FieldControl value shapes ---
  const coerceSessionId = (val) => {
    if (val == null) return null;
    if (typeof val === "string" || typeof val === "number") return String(val);
    if (Array.isArray(val)) return val.length ? coerceSessionId(val[0]) : null;
    if (typeof val === "object") {
      return (
        val.Value ??
        val.value ??
        val._id ??
        val.Id ??
        val.id ??
        val.Key ??
        null
      );
    }
    return null;
  };

  const handleSelectSession = (valueOrItem, maybeItem) => {
    const id = coerceSessionId(valueOrItem) ?? coerceSessionId(maybeItem);
    if (!id) return;

    // Pin UI to chat immediately and reflect selection
    localPinnedRef.current = true;
    setUiMode("chat");
    setSelectedSessionId(id);
    setChatSession((prev) =>
      prev ?? { _id: id, ReferenceId: referenceId, ReferenceItemName: referenceName }
    );

    // Per-session storage; nothing to clear globally
    setSessionChatMessages(undefined);
    setIsLoadingHistory(true);
    loadSessionById(id);
  };

  const renderChatBody = () =>
    uiMode !== "chat" || isLoadingHistory ? (
      <div
        className="ai-panel-content-right-startup-center ai-chat-content"
        style={{ width: "100%", maxWidth: "none" }}
      >
        <h3>{introMessage ?? "Ask your data a question!"}</h3>

        {/* NEW: visible splash text for the user */}
        <p className="ai-panel-splash-message">
          {getSplashText()}
        </p>

        {(sessionChatMessages === undefined || isLoadingHistory) && <Loading />}

        {jsonData?.length > 0 &&
        sessionChatMessages !== undefined &&
        !isLoadingHistory ? (
          <FieldControl
            disabled={false}
            fieldMetadata={{
              FieldType: SystemTypes.UIFieldType.SearchSelectList,
              DataJson: jsonData,
              PlaceHolder: "Start a new conversation..."
            }}
            value={selectedSessionId ?? null}
            onChange={(value, item) => handleSelectSession(value, item)}
          />
        ) : null}

        {sessionChatMessages !== undefined && !isLoadingHistory && (
          <ButtonControl type={"ok"} disabled={operationName ? false : true} onClick={handleStartChat}>
            Chat
          </ButtonControl>
        )}
      </div>
    ) : (
      <AIChatControl
        // removed key so the chat instance is not remounted mid-send when a new session is created
        ref={chatRef}
        placeholder={
          operationName
            ? getSplashText() // use splash text as chat placeholder
            : "AI chat currently not supported"
        }
        onInit={handleInit}
        onSend={handleSend}
        onSave={handleSave}
        thinking={thinking}
        disabled={operationName ? false : true}
        onOpenDetails={handleOpenDetails}
      />
    );

  // ---------- Pane builders ----------
  const withAiTab = (tabs, defaultKey = "ai") => {
    const aiIdx = tabs.findIndex(
      (t) =>
        t.key?.toLowerCase?.() === "ai" ||
        String(t.title || t.label || "").toLowerCase() === "ai"
    );

    const aiTab = {
      key: "ai",
      title: "AI",
      content: (
        <div
          className="ai-panel-right-stretch"
          style={{
            height: "100%",
            width: "100%",
            display: "flex",
            alignItems: "stretch",
            flex: "1 1 auto"
          }}
        >
          {renderChatBody()}
        </div>
      )
    };

    if (aiIdx >= 0) {
      const clone = tabs.slice();
      clone[aiIdx] = { ...clone[aiIdx], ...aiTab };
      return { tabs: clone, defaultKey };
    }
    return { tabs: [aiTab, ...tabs], defaultKey };
  };

  const buildPane = (side, input) => {
    const isChatHere = chatPane === side;

    let reactNode = null;
    let tabsArray = null;

    if (isTabsArray(input)) {
      tabsArray = input;
    } else if (input != null) {
      reactNode = input;
    }

    if (side === "left" && !tabsArray) {
      const manualLeft = (
        <>
          {!dragging || !enableContentDragHiding ? (
            children
          ) : (
            <DragOverlay>Resizing...</DragOverlay>
          )}
          {reactNode}
        </>
      );
      reactNode = manualLeft;
    }

    if (isChatHere) {
      if (tabsArray) {
        const { tabs, defaultKey } = withAiTab(tabsArray, "ai");
        return (
          <TabPaneControl
            className="ai-panel-tabs"
            tabs={tabs}
            defaultKey={defaultKey}
          />
        );
      }

      const chat = (
        <div
          className="ai-panel-right-stretch"
          style={{
            height: "100%",
            width: "100%",
            display: "flex",
            alignItems: "stretch",
            flex: "1 1 auto"
          }}
        >
          {renderChatBody()}
        </div>
      );

      if (!reactNode) return chat;

      return (
        <TabPaneControl
          className="ai-panel-tabs"
          tabs={[
            { key: "ai", title: "AI", content: chat },
            { key: "manual", title: "Manual", content: reactNode }
          ]}
          defaultKey="ai"
        />
      );
    }

    if (tabsArray) {
      return (
        <TabPaneControl className="ai-panel-tabs" tabs={tabsArray} />
      );
    }
    return reactNode ?? null;
  };

  const leftPane = buildPane("left", leftInput);
  const middlePane = buildPane("middle", middleInput);
  const rightPane = buildPane("right", rightInput);
  const paneCount =
    (leftPane ? 1 : 0) +
    (rightPane ? 1 : 0) +
    (middlePane ? 1 : 0);

  return (
    <>
      {toasterMessage && (
        <ToastNotifications position={"top"} message={toasterMessage} />
      )}

      <DialogControl
        openDialog={showChatDialog}
        onX={() => {
          setShowChatDialog(false);
          setSelectedChat(null);
        }}
        hideButtonRow={true}
        canFullScreen={false}
        canOpenNewWindow={false}
        title={"Chat Details"}
        width={1000}
        subTitle={null}
      >
        <div>
          {selectedChat ? (
            <AIChatDetailsControl
              aiChat={selectedChat}
              defaultTab={"flow"}
            />
          ) : (
            <Loading />
          )}
        </div>
      </DialogControl>

      <div
        className={["ai-panel-content", className]
          .filter(Boolean)
          .join(" ")}
      >
        <SplitPaneControl
          panes={paneCount}
          storageKey={storageKey}
          initialRightPct={initialRightPct ?? 0.33}
          initialLeftPct={initialLeftPct ?? 0.33}
          minLeft={minLeft ?? 360}
          minRight={minRight ?? 360}
          minMiddle={minMiddle ?? 360}
          onDragStart={() => setDraging(true)}
          onDragEnd={() => setDraging(false)}
          left={leftPane}
          middle={middlePane || undefined}
          right={rightPane}
        />
      </div>
    </>
  );
};
